import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  DollarSign,
  Package,
  BarChart3,
  Calendar,
  Building2,
  Download,
  FileText
} from 'lucide-react';
import { getSalesReport } from '../../../api/wholesale/reports';
import { getCompanies } from '../../../api/wholesale/companies';
import { exportSalesReport, downloadFile } from '../../../api/wholesale/exports';
import { useFiscalPeriod } from '../../../contexts/FiscalPeriodContext';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const SalesReportPage = () => {
  const { periodRange, getDateRangeParams, periodLabel } = useFiscalPeriod();
  const [reportData, setReportData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    manufacturing_company_id: '',
    order_status: ''
  });

  const fetchCompanies = async () => {
    try {
      const response = await getCompanies();
      const data = response?.data || response || [];
      setCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      // Build params - use fiscal period if no custom dates
      const params = {};

      // Use custom dates if provided, otherwise use fiscal period
      if (filters.date_from || filters.date_to) {
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;
      } else {
        const dateParams = getDateRangeParams();
        if (dateParams.start_date) params.date_from = dateParams.start_date;
        if (dateParams.end_date) params.date_to = dateParams.end_date;
        if (dateParams.ignore_global_period) params.ignore_global_period = true;
      }

      if (filters.manufacturing_company_id)
        params.manufacturing_company_id = filters.manufacturing_company_id;
      if (filters.order_status) params.order_status = filters.order_status;

      const response = await getSalesReport(params);
      setReportData(response?.data || response);
    } catch (err) {
      console.error('Error fetching sales report:', err);
      setError('Failed to load sales report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
    fetchReport();
  }, [periodRange]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    fetchReport();
  };

  const handleExport = async (format) => {
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.manufacturing_company_id)
        params.manufacturing_company_id = filters.manufacturing_company_id;
      if (filters.order_status) params.order_status = filters.order_status;

      const response = await exportSalesReport(params, format);

      if (response.success && response.data?.file_url) {
        // Download the file
        window.open(response.data.file_url, '_blank');
        alert(`Sales report exported successfully as ${format.toUpperCase()}`);
      }
    } catch (err) {
      console.error('Error exporting sales report:', err);
      alert(err.message || 'Failed to export sales report');
    }
  };

  const handleResetFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      manufacturing_company_id: '',
      order_status: ''
    });
    setTimeout(() => fetchReport(), 100);
  };

  if (loading && !reportData) {
    return <LoadingSpinner />;
  }

  const summary = reportData?.summary || {};
  const revenueByCompany = reportData?.revenue_by_company || [];
  const revenueByStatus = reportData?.revenue_by_status || [];
  const dailyTrend = reportData?.daily_trend || [];
  const topProducts = reportData?.top_products || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Report</h1>
          <p className="text-gray-500 mt-1">Analyze sales performance and revenue trends</p>
        </div>
        {/* Fiscal Period Badge */}
        <span className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-sm rounded-full border border-emerald-200">
          <Calendar className="h-4 w-4" />
          {periodLabel || 'All Time'}
        </span>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Manufacturing Company
            </label>
            <select
              value={filters.manufacturing_company_id}
              onChange={(e) => handleFilterChange('manufacturing_company_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">All Companies</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.company_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order Status</label>
            <select
              value={filters.order_status}
              onChange={(e) => handleFilterChange('order_status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="delivered">Delivered</option>
              <option value="in_transit">In Transit</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Apply Filters
          </button>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Reset
          </button>
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => handleExport('csv')}
              className="flex items-center gap-2 px-4 py-2 border border-emerald-600 text-emerald-600 rounded-lg hover:bg-emerald-50"
              title="Export as CSV"
            >
              <FileText className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport('excel')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              title="Export as Excel"
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Total Orders</p>
            <Package className="h-5 w-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{summary.total_orders || 0}</p>
          <div className="mt-2 flex gap-3 text-xs">
            <span className="text-green-600">{summary.completed_orders || 0} completed</span>
            <span className="text-red-600">{summary.cancelled_orders || 0} cancelled</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Total Revenue</p>
            <DollarSign className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ₦{parseFloat(summary.total_revenue || 0).toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Completed: ₦{parseFloat(summary.completed_revenue || 0).toLocaleString()}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Avg Order Value</p>
            <TrendingUp className="h-5 w-5 text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ₦{parseFloat(summary.average_order_value || 0).toLocaleString()}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Total Tonnage</p>
            <BarChart3 className="h-5 w-5 text-orange-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {parseFloat(summary.total_tonnage || 0).toFixed(2)} tons
          </p>
        </div>
      </div>

      {/* Charts Section */}
      {dailyTrend.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Trend Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              Daily Revenue Trend
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="order_date"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value) => `₦${parseFloat(value).toLocaleString()}`}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="Revenue"
                  dot={{ fill: '#10b981', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Order Volume Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-600" />
              Daily Order Volume
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="order_date"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend />
                <Bar dataKey="order_count" fill="#3b82f6" name="Orders" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Company Revenue Distribution Chart */}
      {revenueByCompany.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-purple-600" />
              Revenue Distribution by Company
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={revenueByCompany}
                  dataKey="total_revenue"
                  nameKey="company_name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ company_name, percent }) =>
                    `${company_name}: ${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={false}
                >
                  {revenueByCompany.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899'][index % 5]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => `₦${parseFloat(value).toLocaleString()}`}
                  contentStyle={{ fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Bar Chart - Company Comparison */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-orange-600" />
              Revenue Comparison
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueByCompany}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="company_name"
                  tick={{ fontSize: 11 }}
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value) => `₦${parseFloat(value).toLocaleString()}`}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend />
                <Bar dataKey="total_revenue" fill="#10b981" name="Revenue" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Revenue by Company Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Building2 className="h-5 w-5 text-emerald-600" />
          Revenue by Manufacturing Company
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Orders
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Avg Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Tonnage
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {revenueByCompany.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No data available
                  </td>
                </tr>
              ) : (
                revenueByCompany.map((company, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {company.company_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{company.order_count}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-green-600">
                      ₦{parseFloat(company.total_revenue || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      ₦{parseFloat(company.avg_order_value || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {parseFloat(company.total_tonnage || 0).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Revenue by Status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Revenue by Order Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {revenueByStatus.map((status, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500 capitalize mb-1">
                {status.order_status.replace('_', ' ')}
              </p>
              <p className="text-xl font-bold text-gray-900">
                ₦{parseFloat(status.total_revenue || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {status.order_count} orders • Avg: ₦
                {parseFloat(status.avg_order_value || 0).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Top Products */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Top Products by Revenue</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Orders
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Revenue
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {topProducts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No data available
                  </td>
                </tr>
              ) : (
                topProducts.map((product, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-bold text-gray-500">#{idx + 1}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {product.product_name}
                      </div>
                      <div className="text-sm text-gray-500">{product.product_code}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{product.order_count}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {parseFloat(product.total_quantity || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-green-600">
                      ₦{parseFloat(product.total_revenue || 0).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily Trend */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-emerald-600" />
          Daily Revenue Trend (Last 30 Days)
        </h2>
        <div className="space-y-2">
          {dailyTrend.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No trend data available</p>
          ) : (
            dailyTrend.map((day, idx) => {
              const maxRevenue = Math.max(...dailyTrend.map((d) => parseFloat(d.revenue || 0)));
              const widthPercent =
                maxRevenue > 0 ? (parseFloat(day.revenue || 0) / maxRevenue) * 100 : 0;

              return (
                <div key={idx} className="flex items-center gap-4">
                  <div className="w-24 text-sm text-gray-600">
                    {new Date(day.date).toLocaleDateString()}
                  </div>
                  <div className="flex-1">
                    <div className="bg-gray-200 rounded-full h-6 relative">
                      <div
                        className="bg-emerald-500 h-6 rounded-full flex items-center px-3 text-xs font-medium text-white"
                        style={{ width: `${widthPercent}%`, minWidth: '60px' }}
                      >
                        ₦{parseFloat(day.revenue || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="w-20 text-sm text-gray-600 text-right">
                    {day.order_count} orders
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesReportPage;
